/*
* Copyright Gregory Coburn 2020-2026, All Rights Reserved, See license for further details
*/
import { Observable, of } from 'rxjs';
import { switchMap } from 'rxjs/operators';
import { MatDialog } from '@angular/material/dialog';
import { AbstractObject, uuid } from '@src/app/model/abstract-object';
import { AbstractHttpService } from '@src/app/shared/abstract-http.service';
import { Field } from '@src/app/shared/field/Field';
import { FieldMaker } from '@src/app/shared/field/FieldMaker';
import { FieldSet } from '@src/app/shared/form/field-set/field-set.component';
import { FormConfig } from '@src/app/shared/form/FormConfig';
import { EditDialogComponent } from '@src/app/shared/dialogs/edit-dialog/edit-dialog.component';
import { MyInjector } from '@src/app/app.module';

/**
 * A focused single-column edit popup for one section of a page - the fields it edits are a
 * subset of the record, built fresh for the dialog (a Field owns one control, so the page's own
 * instances can't be reused). Subclass per section: give it the service, a title and the field
 * list; override loadRefData for picklists and beforeEdit for anything that depends on the
 * loaded record. edit(id) opens the dialog and emits the saved record, or undefined on cancel.
 */
export abstract class SectionEditor<T extends AbstractObject = AbstractObject> {
    protected dialog = MyInjector.instance.get(MatDialog);
    protected abstract svc: AbstractHttpService;
    protected abstract title: string;
    protected width = 560;

    protected loadRefData(): Observable<unknown> {
        return of(null);
    }

    protected abstract buildFields(): Field[];

    protected beforeEdit(o: T): T {
        return o;
    }

    edit(id: uuid): Observable<T | undefined> {
        return this.loadRefData().pipe(switchMap(() => {
            const config = new FormConfig({
                title: this.title,
                fieldSet: new FieldSet({
                    // teamId is held even though no section edits it: an agent managed record is
                    // saved to the tenant its teamId names - see AbstractAgentManagedHttpService.
                    fields: [
                        FieldMaker.id(), FieldMaker.rev(), FieldMaker.idHolder('teamId'),
                        ...this.buildFields()
                    ],
                    formLayout: [{ cells: [{}] }],
                }),
                beforeEdit: this.beforeEdit.bind(this),
            });
            return this.dialog.open(EditDialogComponent, {
                data: { config, service: this.svc, id, width: this.width, hideTabs: true },
            }).afterClosed();
        }));
    }
}
